import {
  Box,
  Card,
  rem,
  Stack,
  Text,
  ThemeIcon,
  Badge,
  Tooltip,
  ActionIcon,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import {
  IconTrophy,
  IconX,
  IconMinus,
  IconClock,
  IconEdit,
  IconFlag,
} from "@tabler/icons-react";
import { MatchScoreStatus } from "../../../../gql/graphql";

const useStyles = createStyles((theme) => ({
  card: {
    backgroundColor: theme.colors.dark[6],
    border: `1px solid ${theme.colors.dark[4]}`,
    borderRadius: theme.radius.md,
    padding: theme.spacing.md,
    transition: "border-color 200ms ease",
    position: "relative",
    overflow: "visible",
    "&:hover": {
      border: `1px solid ${theme.colors.dark[3]}`,
    },
  },
  cardLive: {
    borderColor: theme.colors.green[7],
  },
  cardCompleted: {
    opacity: 0.85,
    borderColor: theme.colors.dark[5],
  },
  matchNumber: {
    position: "absolute",
    top: rem(-12),
    left: rem(12),
    backgroundColor: theme.colors.dark[7],
    padding: `${rem(2)} ${rem(10)}`,
    borderRadius: theme.radius.sm,
    fontSize: theme.fontSizes.xs,
    fontWeight: 600,
    color: theme.colors.cyan[4],
    border: `1px solid ${theme.colors.dark[5]}`,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  liveBadge: {
    position: "absolute",
    top: rem(-12),
    right: rem(12),
  },
  teamRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: `${rem(10)} ${rem(12)}`,
    borderRadius: theme.radius.sm,
    transition: "all 150ms ease",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.colors.dark[5],
    },
  },
  teamRowWinner: {
    backgroundColor: `${theme.colors.green[9]}15`,
    "&:hover": {
      backgroundColor: `${theme.colors.green[9]}20`,
    },
  },
  teamRowLoser: {
    backgroundColor: `${theme.colors.rose[9]}10`,
    opacity: 0.8,
    "&:hover": {
      backgroundColor: `${theme.colors.rose[9]}15`,
    },
  },
  teamRowForfeit: {
    backgroundColor: `${theme.colors.amber[9]}10`,
    textDecoration: "line-through",
    opacity: 0.7,
  },
  teamInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
    flex: 1,
    minWidth: 0,
  },
  teamIcon: {
    flexShrink: 0,
  },
  teamName: {
    fontSize: theme.fontSizes.md,
    fontWeight: 500,
    color: theme.colors.dark[0],
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  teamNameWinner: {
    color: theme.colors.green[4],
    fontWeight: 600,
  },
  teamNameLoser: {
    color: theme.colors.dark[3],
  },
  scoreSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.xs,
    flexShrink: 0,
  },
  score: {
    fontSize: theme.fontSizes.xl,
    fontWeight: 700,
    minWidth: rem(32),
    textAlign: "center",
    fontFamily: "monospace",
  },
  scoreWinner: {
    color: theme.colors.green[4],
  },
  scoreLoser: {
    color: theme.colors.rose[5],
  },
  scoreWaiting: {
    color: theme.colors.dark[4],
  },
  divider: {
    height: rem(1),
    backgroundColor: theme.colors.dark[5],
    margin: `${rem(8)} 0`,
  },
  vsText: {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: theme.colors.dark[6],
    padding: `${rem(4)} ${rem(12)}`,
    borderRadius: theme.radius.xl,
    fontSize: theme.fontSizes.xs,
    fontWeight: 700,
    color: theme.colors.dark[3],
    textTransform: "uppercase",
    letterSpacing: "0.15em",
    border: `1px solid ${theme.colors.dark[5]}`,
    zIndex: 1,
  },
  matchInfo: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: theme.spacing.md,
    marginTop: theme.spacing.sm,
    padding: `${rem(6)} ${rem(12)}`,
    backgroundColor: theme.colors.dark[7],
    borderRadius: theme.radius.sm,
    fontSize: theme.fontSizes.xs,
    color: theme.colors.dark[2],
  },
  editButton: {
    opacity: 0.6,
    transition: "opacity 150ms ease",
    "&:hover": {
      opacity: 1,
    },
  },
  matchDetailsRow: {
    display: "flex",
    alignItems: "center",
    gap: rem(4),
    marginLeft: "auto",
    flexShrink: 0,
  },
  matchDot: {
    width: rem(24),
    height: rem(24),
    borderRadius: theme.radius.sm,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: theme.fontSizes.xs,
    fontWeight: 700,
    fontFamily: "monospace",
  },
}));

interface MatchDetail {
  matchId: string;
  score: number;
  status: MatchScoreStatus;
}

interface TeamScore {
  teamId: string;
  teamName: string;
  status: MatchScoreStatus;
  score: number;
  matchDetails?: MatchDetail[];
}

interface MultiTeamScore {
  teamId: string;
  teamName: string;
  score: number | null;
  logoUrl?: string;
  status?: string;
  matchDetails?: MatchDetail[];
}

interface MatchScoreCardProps {
  matchNumber?: number;
  team1: TeamScore | null;
  team2: TeamScore | null;
  teams?: MultiTeamScore[];
  bestOf?: number;
  isLive?: boolean;
  isCompleted?: boolean;
  showVs?: boolean;
  onTeamClick?: (team: TeamScore) => void;
  onEditClick?: () => void;
}

const getStatusIcon = (status: MatchScoreStatus, size = 16) => {
  switch (status) {
    case MatchScoreStatus.Winner:
      return <IconTrophy size={size} />;
    case MatchScoreStatus.Loser:
      return <IconX size={size} />;
    case MatchScoreStatus.Forfeit:
      return <IconFlag size={size} />;
    default:
      return <IconClock size={size} />;
  }
};

const getStatusColor = (status: MatchScoreStatus) => {
  switch (status) {
    case MatchScoreStatus.Winner:
      return "green";
    case MatchScoreStatus.Loser:
      return "rose";
    case MatchScoreStatus.Forfeit:
      return "amber";
    default:
      return "dark";
  }
};

export const MatchScoreCard = ({
  matchNumber,
  team1,
  team2,
  teams,
  bestOf,
  isLive = false,
  isCompleted = false,
  showVs = true,
  onTeamClick,
  onEditClick,
}: MatchScoreCardProps) => {
  const { classes, cx } = useStyles();

  const renderTeamRow = (team: TeamScore | null) => {
    if (!team) {
      return (
        <Box className={classes.teamRow}>
          <div className={classes.teamInfo}>
            <ThemeIcon size="sm" variant="light" color="dark" radius="xl">
              <IconMinus size={12} />
            </ThemeIcon>
            <Text className={classes.teamName} c="dimmed" fs="italic">
              TBD
            </Text>
          </div>
          <div className={classes.scoreSection}>
            <Text className={cx(classes.score, classes.scoreWaiting)}>-</Text>
          </div>
        </Box>
      );
    }

    const isWinner = team.status === MatchScoreStatus.Winner;
    const isLoser = team.status === MatchScoreStatus.Loser;
    const isForfeit = team.status === MatchScoreStatus.Forfeit;
    const isWaiting = team.status === MatchScoreStatus.Waiting;

    return (
      <Box
        className={cx(classes.teamRow, {
          [classes.teamRowWinner]: isWinner,
          [classes.teamRowLoser]: isLoser,
          [classes.teamRowForfeit]: isForfeit,
        })}
        onClick={() => onTeamClick?.(team)}
      >
        <div className={classes.teamInfo}>
          <Tooltip label={team.status} position="left">
            <ThemeIcon
              size="sm"
              variant="light"
              color={getStatusColor(team.status)}
              radius="xl"
              className={classes.teamIcon}
            >
              {getStatusIcon(team.status, 12)}
            </ThemeIcon>
          </Tooltip>
          <Text
            className={cx(classes.teamName, {
              [classes.teamNameWinner]: isWinner,
              [classes.teamNameLoser]: isLoser || isForfeit,
            })}
          >
            {team.teamName}
          </Text>
        </div>
        <div className={classes.scoreSection}>
          {team.matchDetails && team.matchDetails.length > 1 ? (
            <div className={classes.matchDetailsRow}>
              {team.matchDetails.map((md, idx) => {
                const mdColor = getStatusColor(md.status);
                return (
                  <Tooltip
                    key={md.matchId}
                    label={`Match ${idx + 1}: ${md.status}`}
                  >
                    <Box
                      className={classes.matchDot}
                      style={{
                        backgroundColor: `var(--mantine-color-${mdColor}-9)`,
                        color: `var(--mantine-color-${mdColor}-4)`,
                      }}
                    >
                      {md.score}
                    </Box>
                  </Tooltip>
                );
              })}
            </div>
          ) : (
            <Text
              className={cx(classes.score, {
                [classes.scoreWinner]: isWinner,
                [classes.scoreLoser]: isLoser || isForfeit,
                [classes.scoreWaiting]: isWaiting,
              })}
            >
              {isWaiting ? "-" : team.score}
            </Text>
          )}
        </div>
      </Box>
    );
  };

  return (
    <Card
      className={cx(classes.card, {
        [classes.cardLive]: isLive,
        [classes.cardCompleted]: isCompleted,
      })}
    >
      {matchNumber !== undefined && (
        <div className={classes.matchNumber}>
          Game {matchNumber}
          {isLive && " - STARTED"}
          {isCompleted && " - ENDED"}
        </div>
      )}

      {isLive && (
        <Badge
          className={classes.liveBadge}
          color="green"
          variant="filled"
          size="sm"
        >
          LIVE
        </Badge>
      )}

      {onEditClick && (
        <ActionIcon
          size="sm"
          variant="subtle"
          color="cyan"
          className={classes.editButton}
          onClick={onEditClick}
          style={{ position: "absolute", top: 8, right: 8, zIndex: 2 }}
        >
          <IconEdit size={14} />
        </ActionIcon>
      )}

      {teams && teams.length > 0 ? (
        <Stack gap={0} mt={matchNumber !== undefined ? "xs" : 0}>
          {teams.map((multiTeam, idx) => {
            const teamScore: TeamScore = {
              teamId: multiTeam.teamId,
              teamName: multiTeam.teamName,
              status:
                (multiTeam.status as MatchScoreStatus) ??
                MatchScoreStatus.Waiting,
              score: multiTeam.score ?? 0,
              matchDetails: multiTeam.matchDetails,
            };
            return (
              <Box key={multiTeam.teamId}>
                {idx > 0 && <div className={classes.divider} />}
                {renderTeamRow(teamScore)}
              </Box>
            );
          })}
        </Stack>
      ) : (
        <Stack
          gap={0}
          mt={matchNumber !== undefined ? "xs" : 0}
          style={{ position: "relative" }}
        >
          {renderTeamRow(team1)}

          <div className={classes.divider} />
          {showVs && <div className={classes.vsText}>VS</div>}

          {renderTeamRow(team2)}
        </Stack>
      )}

      {bestOf && (
        <div className={classes.matchInfo}>
          <Text>Best of {bestOf}</Text>
        </div>
      )}
    </Card>
  );
};

export default MatchScoreCard;
